import { useEffect, useState } from "react";
import API from "../../lib/api";
import { PortalPage, StatCard, Section } from "../../components/portal/PortalPage";
import { fmtMoney } from "../../components/portal/portalUtils";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { ClipboardText, Briefcase, Lightning, CurrencyGbp, Timer, ShieldCheck } from "@phosphor-icons/react";
import { StatsGridSkeleton } from "../../components/Skeletons";
import { ByClientReport, StatementDownload } from "../../components/portal/ByClientReport";
import { FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

export default function ClientReportsPage() {
  const [months, setMonths] = useState(12);
  const [d, setD] = useState(null);
  const fam = useFamilyFilter();
  const focusName = fam.selected ? (fam.selected === "__self__" ? fam.self?.display_name : fam.selectedSub?.display_name) : "";
  useEffect(() => { setD(null); API.get(`/client-hub/reports?months=${months}${fam.selected ? `&client_focus=${fam.selected}` : ""}`).then(({ data }) => setD(data)).catch(() => setD({ kpis: {}, spend_by_month: [], jobs_completed_by_month: [], requests_by_month: [], spend_by_property: [], requests_by_category: [] })); }, [months, fam.selected]);
  const k = d?.kpis || {};

  return (
    <PortalPage
      title="Reports"
      subtitle={focusName ? `Spend, activity and response performance for ${focusName}.` : "Spend, activity and response performance across your portfolio."}
      testid="client-reports"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <FamilyFilter fam={fam} testid="reports-client-filter" />
          {d?.by_client && <StatementDownload />}
          <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
            {[6, 12, 24].map((m) => <button key={m} onClick={() => setMonths(m)} className={`px-3 py-1 text-xs rounded ${months === m ? "bg-card shadow font-semibold" : "text-muted-foreground"}`} data-testid={`reports-range-${m}`}>{m} months</button>)}
          </div>
        </div>
      }
    >
      {!d ? <StatsGridSkeleton cols={4} /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard icon={CurrencyGbp} label="Total Invoiced" value={fmtMoney(k.total_invoiced)} color="text-blue-500" sub={`${fmtMoney(k.paid)} paid · ${fmtMoney(k.outstanding)} outstanding`} testid="rep-invoiced" />
            <StatCard icon={ClipboardText} label="Requests Raised" value={k.total_requests ?? 0} color="text-amber-500" sub={`${k.mandate_requests ?? 0} with price mandate`} testid="rep-requests" />
            <StatCard icon={Briefcase} label="Jobs Completed" value={`${k.completed_jobs ?? 0} / ${k.total_jobs ?? 0}`} color="text-emerald-500" sub={k.avg_request_to_completion_days != null ? `Avg ${k.avg_request_to_completion_days} days request → done` : "No completed cycle yet"} testid="rep-jobs" />
            <StatCard icon={Lightning} label="Emergencies" value={k.emergencies ?? 0} color="text-red-500" sub={k.avg_emergency_response_hours != null ? `Avg response ${k.avg_emergency_response_hours}h` : "No emergencies logged"} testid="rep-emergencies" />
          </div>
          {d.by_client && <div className="mb-6"><ByClientReport rows={d.by_client} /></div>}
          <div className="grid lg:grid-cols-2 gap-6">
            <Section title="Spend by Month" testid="chart-spend">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.spend_by_month.map((r) => ({ ...r, label: monthLabel(r.month) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="opacity-10" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `£${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="amount" fill="#2563eb" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
            <Section title="Activity by Month" testid="chart-activity">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.requests_by_month.map((r, i) => ({ label: monthLabel(r.month), requests: r.count, completed: d.jobs_completed_by_month[i]?.count || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="opacity-10" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="requests" stroke="#f59e0b" strokeWidth={2} dot={false} name="Requests raised" />
                    <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} dot={false} name="Jobs completed" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 text-[10px] text-muted-foreground mt-2"><span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> Requests raised</span><span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Jobs completed</span></div>
            </Section>
            <Section title="Spend by Property" testid="spend-by-property">
              {d.spend_by_property.length === 0 ? <p className="text-xs text-muted-foreground">No invoiced spend yet.</p> : (
                <ul className="space-y-2">
                  {d.spend_by_property.map((p) => {
                    const max = d.spend_by_property[0].amount || 1;
                    return (
                      <li key={p.property} className="text-xs">
                        <div className="flex justify-between gap-2 mb-0.5"><span className="truncate">{p.property}</span><span className="font-semibold shrink-0">{fmtMoney(p.amount)}</span></div>
                        <div className="h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${Math.max(3, (p.amount / max) * 100)}%` }} /></div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>
            <Section title="Requests by Category" testid="requests-by-category">
              {d.requests_by_category.length === 0 ? <p className="text-xs text-muted-foreground">No requests yet.</p> : (
                <ul className="space-y-2">
                  {d.requests_by_category.map((c) => {
                    const max = d.requests_by_category[0].count || 1;
                    return (
                      <li key={c.category} className="text-xs">
                        <div className="flex justify-between gap-2 mb-0.5"><span>{c.category}</span><span className="font-semibold">{c.count}</span></div>
                        <div className="h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${Math.max(3, (c.count / max) * 100)}%` }} /></div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-3 text-xs">
            <div className="border border-border rounded-md p-3 flex items-center gap-3"><Timer size={20} className="text-emerald-500" weight="duotone" /><div><p className="font-semibold">Request → completion</p><p className="text-muted-foreground">{k.avg_request_to_completion_days != null ? `Average ${k.avg_request_to_completion_days} days from submission to job completion` : "Available once the first request completes"}</p></div></div>
            <div className="border border-border rounded-md p-3 flex items-center gap-3"><ShieldCheck size={20} className="text-blue-500" weight="duotone" /><div><p className="font-semibold">Price mandates</p><p className="text-muted-foreground">{k.mandate_requests ?? 0} of {k.total_requests ?? 0} requests used a mandate to skip quoting</p></div></div>
          </div>
        </>
      )}
    </PortalPage>
  );
}
